import { useMemo, useState } from 'react';
import type { GenerationPreset } from '@mpr/shared';

import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import {
  diffAgainstPreset,
  displayValue,
  rowsToFormPatch,
  type ContractField,
  type ContractSnapshot,
} from '../lib/catalogueDiff.js';
import { ChangeRow } from './ConnectorChangeRow.js';

/**
 * "Update from catalogue": the catalogue entry a connector started from moves
 * on (a model is retired, a path changes); this shows what differs, field by
 * field, and puts the ticked rows into the form. Nothing is saved from here:
 * the person reviews the form and presses Save, which also resets the test.
 */

interface Props {
  preset: GenerationPreset;
  /** The contract as the form holds it now (so unsaved edits are compared too). */
  current: ContractSnapshot;
  capability: 'SCRIPT' | 'AUDIO' | 'VIDEO' | 'IMAGE';
  onApply: (patch: Partial<Record<ContractField, string | boolean>>) => void;
}

export function CatalogueRefresh({ preset, current, capability, onApply }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  // Which rows the person has toggled away from their default; the rest follow it.
  const [flipped, setFlipped] = useState<ReadonlySet<ContractField>>(new Set());
  const [applied, setApplied] = useState(false);

  const rows = useMemo(() => diffAgainstPreset(current, preset, capability), [current, preset, capability]);
  const isChecked = (field: ContractField, byDefault: boolean) => (flipped.has(field) ? !byDefault : byDefault);
  const chosen = rows.filter((row) => isChecked(row.field, row.checkedByDefault));

  const toggle = (field: ContractField) => {
    const next = new Set(flipped);
    if (next.has(field)) next.delete(field);
    else next.add(field);
    setFlipped(next);
    setApplied(false);
  };

  return (
    <div className="catalogue-refresh">
      <button
        type="button"
        className="ghost small"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          setApplied(false);
        }}
      >
        {t('studioConnectors.catalogue.button')}
      </button>

      {open && (
        <div className="catalogue-panel">
          <p className="muted small">{t('studioConnectors.catalogue.hint', { label: preset.label })}</p>

          {rows.length === 0 ? (
            <p className="ok-text small">{t('studioConnectors.catalogue.same')}</p>
          ) : (
            <>
              <ul className="change-list">
                {rows.map((row) => (
                  <ChangeRow
                    key={row.field}
                    label={t(`studioConnectors.field.${row.field}` as TranslationKey)}
                    before={displayValue(row.before)}
                    after={displayValue(row.after)}
                    note={row.checkedByDefault ? undefined : t('studioConnectors.catalogue.customised')}
                    checked={isChecked(row.field, row.checkedByDefault)}
                    onToggle={() => toggle(row.field)}
                  />
                ))}
              </ul>
              <div className="actions">
                <button
                  type="button"
                  className="primary"
                  disabled={chosen.length === 0}
                  onClick={() => {
                    onApply(rowsToFormPatch(chosen));
                    setApplied(true);
                  }}
                >
                  {t('studioConnectors.catalogue.apply', { n: chosen.length })}
                </button>
              </div>
            </>
          )}
          {applied && <p className="ok-text small">{t('studioConnectors.catalogue.applied')}</p>}
        </div>
      )}
    </div>
  );
}
